import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  CreditCard, 
  Plus, 
  Trash2, 
  Star, 
  RefreshCw,
  AlertCircle,
  CheckCircle,
  ExternalLink,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { CardSetupShareModal } from '@/components/shared/CardSetupShareModal';

interface PaymentMethod {
  id: string;
  type: string;
  card: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  } | null;
  is_default: boolean;
  created: number;
}

interface FinanceiroTabProps {
  contactId: string;
  contactName?: string;
  contactPhone?: string;
}

const CARD_BRAND_COLORS: Record<string, string> = {
  visa: 'bg-blue-500',
  mastercard: 'bg-orange-500',
  amex: 'bg-blue-700',
  discover: 'bg-orange-600',
  diners: 'bg-gray-600',
  jcb: 'bg-green-600',
  unionpay: 'bg-red-600',
  default: 'bg-slate-500'
};

export function FinanceiroTab({ contactId, contactName, contactPhone }: FinanceiroTabProps) {
  const queryClient = useQueryClient();
  const [deletePaymentMethodId, setDeletePaymentMethodId] = useState<string | null>(null);
  const [isCreatingLink, setIsCreatingLink] = useState(false);
  const [cardSetupLink, setCardSetupLink] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);

  // Fetch payment methods
  const { 
    data: paymentMethodsData, 
    isLoading, 
    error,
    refetch 
  } = useQuery({
    queryKey: ['payment-methods', contactId],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('stripe-list-payment-methods', {
        body: { contact_id: contactId }
      });

      if (error) throw error;
      return data;
    },
    enabled: !!contactId,
  });

  // Set default payment method mutation
  const setDefaultMutation = useMutation({
    mutationFn: async (paymentMethodId: string) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('stripe-set-default-payment-method', {
        body: { 
          contact_id: contactId,
          payment_method_id: paymentMethodId 
        }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Cartão padrão atualizado');
      queryClient.invalidateQueries({ queryKey: ['payment-methods', contactId] });
    },
    onError: (error: any) => {
      toast.error(`Erro ao atualizar: ${error.message}`);
    }
  });

  // Delete payment method mutation
  const deleteMutation = useMutation({
    mutationFn: async (paymentMethodId: string) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('stripe-delete-payment-method', {
        body: { 
          contact_id: contactId,
          payment_method_id: paymentMethodId 
        }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Cartão removido com sucesso');
      setDeletePaymentMethodId(null);
      queryClient.invalidateQueries({ queryKey: ['payment-methods', contactId] });
    },
    onError: (error: any) => {
      toast.error(`Erro ao remover: ${error.message}`);
      setDeletePaymentMethodId(null);
    }
  });

  // Create payment link for adding a new card
  const handleAddCard = async () => {
    setIsCreatingLink(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('stripe-create-payment-link', {
        body: { 
          contact_id: contactId,
          origin_context: 'contact_finance_setup',
          mode: 'setup',
          success_url: window.location.href,
          cancel_url: window.location.href
        }
      });

      if (error) throw error;

      if (data?.url) {
        setCardSetupLink(data.url);
        setShowShareModal(true);
      }
    } catch (error: any) {
      toast.error(`Erro ao criar link: ${error.message}`);
    } finally {
      setIsCreatingLink(false);
    }
  };

  const paymentMethods: PaymentMethod[] = paymentMethodsData?.payment_methods || [];
  const stripeCustomerId = paymentMethodsData?.stripe_customer_id;
  const hasPaymentMethods = paymentMethods.length > 0;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex flex-col items-center justify-center text-center gap-4">
            <AlertCircle className="w-12 h-12 text-destructive" />
            <div>
              <p className="font-medium text-foreground">Erro ao carregar métodos de pagamento</p>
              <p className="text-sm text-muted-foreground mt-1">{(error as Error).message}</p>
            </div>
            <Button variant="outline" onClick={() => refetch()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Tentar novamente
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Métodos de Pagamento</h2>
          <p className="text-sm text-muted-foreground">
            Gerencie os cartões salvos para cobranças automáticas
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={cn("w-4 h-4 mr-2", isLoading && "animate-spin")} />
            Atualizar
          </Button>
          <Button 
            size="sm"
            onClick={handleAddCard}
            disabled={isCreatingLink}
          >
            {isCreatingLink ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Plus className="w-4 h-4 mr-2" />
            )}
            Adicionar Cartão
          </Button>
        </div>
      </div>

      {/* Stripe Customer Info */}
      {stripeCustomerId && (
        <Card className="bg-muted/30">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span className="text-muted-foreground">Cliente Stripe:</span>
                <code className="px-2 py-0.5 bg-muted rounded text-xs font-mono">
                  {stripeCustomerId}
                </code>
              </div>
              <Button
                variant="ghost"
                size="sm"
                asChild
              >
                <a 
                  href={`https://dashboard.stripe.com/customers/${stripeCustomerId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs"
                >
                  Ver no Stripe
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment Methods List */}
      {!hasPaymentMethods ? (
        <Card>
          <CardContent className="py-12">
            <div className="flex flex-col items-center justify-center text-center gap-4">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                <CreditCard className="w-8 h-8 text-muted-foreground" />
              </div>
              <div>
                <p className="font-medium text-foreground">Nenhum cartão cadastrado</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Adicione um cartão para habilitar cobranças automáticas
                </p>
              </div>
              <Button onClick={handleAddCard} disabled={isCreatingLink}>
                {isCreatingLink ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Adicionar Cartão
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {paymentMethods.map((method) => (
            <Card 
              key={method.id}
              className={cn(
                "transition-all",
                method.is_default && "ring-2 ring-primary"
              )}
            >
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {/* Card Brand Icon */}
                    <div className={cn(
                      "w-12 h-8 rounded flex items-center justify-center text-white text-xs font-bold uppercase",
                      CARD_BRAND_COLORS[method.card?.brand?.toLowerCase() || ''] || CARD_BRAND_COLORS.default
                    )}>
                      {method.card?.brand?.slice(0, 4) || 'Card'}
                    </div>
                    
                    {/* Card Details */}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground">
                          •••• •••• •••• {method.card?.last4 || '****'}
                        </span>
                        {method.is_default && (
                          <Badge variant="secondary" className="text-xs">
                            <Star className="w-3 h-3 mr-1 fill-current" />
                            Padrão
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Expira {method.card?.exp_month?.toString().padStart(2, '0') || '??'}/{method.card?.exp_year || '????'}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {!method.is_default && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDefaultMutation.mutate(method.id)}
                        disabled={setDefaultMutation.isPending}
                      >
                        {setDefaultMutation.isPending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Star className="w-4 h-4 mr-1" />
                            Tornar Padrão
                          </>
                        )}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => setDeletePaymentMethodId(method.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog 
        open={!!deletePaymentMethodId} 
        onOpenChange={(open) => !open && setDeletePaymentMethodId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover Cartão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover este cartão? Esta ação não pode ser desfeita.
              Cobranças automáticas não poderão ser feitas com este cartão.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletePaymentMethodId && deleteMutation.mutate(deletePaymentMethodId)}
              className="bg-destructive hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share Card Setup Link Modal */}
      <CardSetupShareModal
        open={showShareModal}
        onOpenChange={setShowShareModal}
        cardSetupLink={cardSetupLink}
        contactName={contactName}
        contactPhone={contactPhone}
      />
    </div>
  );
}
